import React, { useState, useEffect, useContext } from "react";
import Hoc from "../../components/HOC";
import CustomDropdown from "../../components/CustomDropdown";
import Loader from "../../components/Loader";
import { Box } from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Formik, Form } from "formik";
import { FaSearch, FaPlus } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import AddClientModal from "./AddClientModal";
import EditClientModal from "./EditClientModal";
import ViewClientModal from "./EditClientModal/ViewClientModal";
import { fetchExecutives, getClients } from "../../Services/salesapis";
import { getEmployeeByCompany, getEmployeeNamesByCompany } from "../../Services/employee";
import { ClientContext } from "../../Context/Client";
import { SalesContext } from "../../Context/Sales";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { MdModeEdit } from "react-icons/md";
import CustomToolTip from "../../components/ToolTip";
import { UserContext } from "../../Context/User";
import LOCAL_KEYS, { parseLocalStorageJSON } from "../../helpers/localstorage";
import {
  MOBILE_LV_LABEL_CLASS,
  MOBILE_LV_VALUE_CLASS,
  MOBILE_LV_CARD_CLASS,
  MOBILE_LV_GRID_CLASS,
  MOBILE_LV_LABEL_LAST,
  MOBILE_LV_VALUE_LAST,
} from "../../Constants/mobileLabelValueTable";

// dropdown options
const companyOptions = [
  { value: "UNISOFT", label: "Unisoft LLC" },
  { value: "DATACLOUD", label: "DataCloud" },
];

const statusOptions = [
  { value: "all", label: "All Status" },
  { value: "A", label: "Active" },
  { value: "D", label: "Deactive" },
];

const getInitials = (name = "") => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const firstInitial = parts[0]?.charAt(0)?.toUpperCase() || "U";
  const lastInitial = parts[1]?.charAt(0)?.toUpperCase() || "";
  return `${firstInitial}${lastInitial}`;
};

const Sales = () => {
  const [employeesOptions, setEmployeesOptions] = useState([]);
  const [submittedByOptions, setSubmittedByOptions] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editClientData, setEditClientData] = useState(null);
  const [employeeId, setEmployeeId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [filterValues, setFilterValues] = useState({
    company: companyOptions[0],
    employee: { value: "all", label: "All Consultants" },
    submittedBy: { value: "all", label: "All Users" },
    fromDate: null,
    toDate: null,
    status: statusOptions[1], // Default to Active (A)
  });
  const navigate = useNavigate();
  const { submissions, fetchFilteredSubmissions, addNewClient, updateExistingClient } = useContext(ClientContext);
  const { updateTotalSales } = useContext(SalesContext);
  const location = useLocation();
  const { currentUserDetails } = useContext(UserContext);

  // Determine if current user is a Marketing Executive
  const isMarketingExec = currentUserDetails?.acctType === "MKTG_EXEC";
  // Construct the current user's full name for comparison
  const currentUserFullName = `${currentUserDetails?.firstName || ''} ${currentUserDetails?.lastName || ''}`.trim();

  // Add pagination state
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Add state for total count
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Add state for search loader
  const [isSearching, setIsSearching] = useState(false);

  // Add a dedicated state for totalElements
  const [totalElements, setTotalElements] = useState(0);

  // Add state for edit mode
  const [isEditMode, setIsEditMode] = useState(true);

  // Handle rows per page change
  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
    // fetchFilteredData(filterValues); // Will be triggered by useEffect
  };

  // Load employees based on selected company
  const loadEmployees = async (companyValue) => {
    // Check if password is expired before making API call
    const isPasswordExpired = parseLocalStorageJSON(LOCAL_KEYS.PASSWORD_EXPIRED);
    if (isPasswordExpired) {

      setIsLoadingEmployees(false);
      return [];
    }

    setIsLoadingEmployees(true);
    try {
      const employees = await getEmployeeNamesByCompany(companyValue);

      if (Array.isArray(employees)) {
        const formattedEmployees = employees
          .map(emp => ({
            value: emp.id,
            label: emp.fullName,
          }))
          .filter(emp => emp.label && emp.label.trim() !== '');

        setEmployeesOptions([{ value: "all", label: "All Consultants" }, ...formattedEmployees]);
        return [{ value: "all", label: "All Consultants" }, ...formattedEmployees]; // Return the formatted employees
      } else {
        console.error('Unexpected API response format:', employees);
        toast.error("Invalid employee data format received");
        setEmployeesOptions([]);
        return [];
      }
    } catch (error) {
      console.error("Failed loading employees:", error);
      toast.error("Failed to load employees");
      setEmployeesOptions([]);
      return [];
    } finally {
      setIsLoadingEmployees(false);
    }
  };

  // Update fetchFilteredData to show loader during search
  const fetchFilteredData = async (values) => {
    setIsSearching(true);
    try {
      const { company, employee, submittedBy, fromDate, toDate, status } = values;

      const filters = {
        company: company?.value || "",
        employees: employee?.value === "all" ? "all" : employee?.label || "",
        submittedBy: submittedBy?.value || "",
        fromDate: fromDate ? format(fromDate, "MM-dd-yyyy") : "",
        toDate: toDate ? format(toDate, "MM-dd-yyyy") : "",
        page: currentPage - 1, // backend is 0-indexed
        size: rowsPerPage,
        search: searchInputValue,
        stsCd: status?.value || "A" // Add status filter, default to Active
      };

      setFilterValues(values);
      const { data: responseData, message: backendMessage, totalElements: backendTotalElements, totalPages: backendTotalPages } = await fetchFilteredSubmissions(filters);

      // Always show the table area after a search, regardless of results
      setShowTable(true);

      if (responseData && responseData.length === 0) {
        toast.dismiss(); // Dismiss any existing toasts before showing a new one
        toast.error(backendMessage || "No submissions found for the selected filters.");
      }

      setTotalCount(backendTotalElements || 0);
      setTotalElements(backendTotalElements || 0);
      setTotalPages(backendTotalPages || 1);
      updateTotalSales(backendTotalElements || 0); // Update the context with the count when search is clicked
    } finally {
      setIsSearching(false);
    }
  };

  // Initial data load effect
  useEffect(() => {
    const loadInitialData = async () => {
      // Check if password is expired before making API call
      const isPasswordExpired = parseLocalStorageJSON(LOCAL_KEYS.PASSWORD_EXPIRED);
      if (isPasswordExpired) {  
        setIsPageLoading(false);
        return;
      }

      setIsPageLoading(true);
      try {
        setShowTable(false);
        setTotalCount(0);
        updateTotalSales(0);

        // Only load employees if authenticated
        const token = localStorage.getItem("access_token");
        if (token) {
          // Load employees for default company
          await loadEmployees(filterValues.company.value);
          setEmployeeId(null); // Clear employeeId since 'All' doesn't correspond to a single ID
        }

        // Load executives (optional: also wrap in token check if needed)
        if (token) {
          const executives = await fetchExecutives();
          const submittedOptions = executives.map((user) => ({
            value: user.id,
            label: user.fullName,
          }));
          setSubmittedByOptions([
            { value: "all", label: "All Users" },
            ...submittedOptions,
          ]);
        }
      } catch (error) {
        console.error("Failed loading initial data:", error);
      } finally {
        setIsPageLoading(false);
      }
    };

    loadInitialData();
  }, []);

  // Add a new effect to handle form value changes
  useEffect(() => {
    // Reset count when filter values change
    setShowTable(false);
    setTotalCount(0);
    updateTotalSales(0);
  }, [filterValues.company, filterValues.employee, filterValues.submittedBy, filterValues.fromDate, filterValues.toDate, filterValues.status]);

  // In useEffect for currentPage, rowsPerPage, filterValues changes, call fetchFilteredData with correct values
  useEffect(() => {
    if (showTable) {
      fetchFilteredData(filterValues);
    }
    // eslint-disable-next-line
  }, [currentPage, rowsPerPage]);

  const handleEdit = (row) => {
    setEditClientData(row);
    setIsEditMode(true);
    setShowEditModal(true);
  };

  const handleView = (row) => {
    setEditClientData(row);
    setIsEditMode(false);
    setShowEditModal(true);
  };

  const handleAddClient = async (clientData) => {
    try {
      const payload = {
        tempEmpId: employeeId,
        clientName: clientData.clientName,
        clientLocation: clientData.clientLocation,
        jobTitle: clientData.jobTitle,
        vendor: clientData.vendor,
        rate: clientData.rate,
        notes: clientData.notes,
        stsCd: clientData.status,
      };
      const newClient = await addNewClient(payload);
      toast.success("Submission added successfully!");
      setShowAddModal(false);
      // Reset to active status and refresh data
      setFilterValues(prev => ({
        ...prev,
        status: statusOptions[1] // Reset to Active
      }));
      await fetchFilteredData({
        ...filterValues,
        status: statusOptions[1]
      });
    } catch (error) {
      console.error("Failed to add client:", error);
      toast.error(error.message || "Failed to add submission");
    }
  };

  const handleEditClient = async (clientData) => {
    try {
      const clientPayload = {
        tempEmpId: employeeId,
        clientName: clientData.clientName,
        clientLocation: clientData.clientLocation,
        jobTitle: clientData.jobTitle,
        rate: clientData.rate,
        notes: clientData.notes,
        stsCd: clientData.status,
        vendor: clientData.vendor
      };

      await updateExistingClient(clientData.id, clientPayload);
      toast.success("Submission updated successfully!");
      setShowEditModal(false);
      
      // Reset to active status and refresh data
      setFilterValues(prev => ({
        ...prev,
        status: statusOptions[1] // Reset to Active
      }));
      await fetchFilteredData({
        ...filterValues,
        status: statusOptions[1]
      });
    } catch (error) {
      toast.error(error.message || "Failed to update submission");
    }
  };

  const columns = [
    {
      name: "CLIENT NAME",
      selector: (row) => row.clientName,
      sortable: true,
      cell: (row) => (
        <div className="flex items-center">
          <div className="flex-shrink-0 h-10 w-10">
            <div className="h-10 w-10 rounded-full bg-gray-100 text-[#201F1E] flex items-center justify-center font-semibold">
              {getInitials(row.clientName)}
            </div>
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-[#656464]">
              {row.clientName}
            </div>
          </div>
        </div>
      )
    },
    // Add consultant name column conditionally
    ...(filterValues.employee?.value === "all" ? [{
      name: "CONSULTANT NAME",
      selector: (row) => row.consultantName,
      sortable: true,
      cell: (row) => (
        <div className="text-sm text-gray-900" style={{ minWidth: 130, whiteSpace: 'nowrap' }}>{row.consultantName || "N/A"}</div>
      ),
    }] : []),
    {
      name: "LOCATION",
      selector: (row) => row.clientLocation,
      sortable: true,
      cell: (row) => (
        <div className="text-sm text-gray-900">
          <div className="truncate max-w-[40px]" data-tip data-for={`locationTip-${row.id}`} title={row.clientLocation}>{row.clientLocation}</div>
          <CustomToolTip id={`locationTip-${row.id}`} title={row.clientLocation || "N/A"} />
        </div>
      ),
    },
    {
      name: "VENDOR",
      selector: (row) => row.vendor,
      sortable: true,
      cell: (row) => (
        <div className="text-sm text-gray-900">{row.vendor || "N/A"}</div>
      ),
    },
    {
      name: "JOB TITLE",
      selector: (row) => row.jobTitle,
      sortable: true,
      cell: (row) => (
        <div className="text-sm text-gray-900 max-w-[200px] truncate">
          <div className="truncate max-w-[100px]" data-tip data-for={`jobTitleTip-${row.id}`}>{row.jobTitle}</div>
          <CustomToolTip id={`jobTitleTip-${row.id}`} title={row.jobTitle || "N/A"} />
        </div>
      ),
    },
    {
      name: "RATE",
      selector: (row) => row.rate,
      sortable: true,
      cell: (row) => (
        <div className="text-sm text-gray-900">{row.rate}</div>
      ),
    },
    {
      name: "NOTES",
      selector: (row) => row.notes,
      sortable: true,
      cell: (row) => (
        <div className="text-sm text-gray-900">
          <div className="truncate max-w-[100px]" data-tip data-for={`notesTip-${row.id}`}>
            {row.notes || "N/A"}
          </div>
          <CustomToolTip id={`notesTip-${row.id}`} title={row.notes || "N/A"} />
        </div>
      ),
    },
    {
      name: "SUBMITTED BY",
      selector: row => row.submittedBy,
      sortable: true,
      cell: row => <div className="text-sm text-gray-900">{row.submittedBy}</div>
    },
    {
      name: "UPDATE DATE",
      selector: row => row.updatedDate,
      sortable: true,
      cell: row => {
        const date = new Date(row.updatedDate);
        return <div className="text-sm text-gray-900" style={{ minWidth: 90, whiteSpace: 'nowrap' }}>{format(date, "MM-dd-yyyy")}</div>;
      }
    },
    {
      name: "STATUS",
      selector: (row) => row.stsCd,
      sortable: true,
      cell: (row) => {
        const statusText = row.stsCd === "A" ? "Active" : "Deactive";
        const statusStyles =
          row.stsCd === "A"
            ? "bg-[rgba(52, 211, 153, 0.125)] text-[#34D399]"
            : "bg-red-100 text-red-600";

        return (
          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${row.stsCd === 'A' ? 'bg-[#34D39920] text-[#34D399]' : 'bg-red-50 text-red-400'}`}>{row.stsCd === 'A' ? 'Active' : 'Closed'}</span>
        );
      },
    },
    {
      name: "ACTIONS",
      cell: row => {
        const isDisabled = isMarketingExec && row.submittedBy !== currentUserFullName;
        const buttonClasses = `
          ${isDisabled ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:text-blue-900'}
        `;
        return (
          <div>
            <button
              onClick={e => { e.stopPropagation(); handleEdit(row); }}
              className={buttonClasses}
              disabled={isDisabled}
            >
              <MdModeEdit size={20} data-tip data-for={`editTip-${row.id}`} />
              <CustomToolTip id={`editTip-${row.id}`} title="Edit" />
            </button>
          </div>
        );
      }
    }
  ];

  const clientInitialValues = {
    clientName: "",
    clientLocation: "",
    jobTitle: "",
    rate: "",
    technology: "",
    status: "A",
    notes: "",
    vendor: ""
  };

  // Add handler for status change
  const handleStatusChange = (value) => {
    const newStatus = statusOptions.find(opt => opt.value === value) || statusOptions[1]; // Default to Active
    setFilterValues(prev => ({
      ...prev,
      status: newStatus
    }));
    // Clear table data and update count
    setShowTable(false);
    setTotalCount(0);
    updateTotalSales(0);
    // Trigger search with new status
    fetchFilteredData({
      ...filterValues,
      status: newStatus
    });
  };

  // Add effect for cleanup
  useEffect(() => {
    // Cleanup function
    return () => {
      // Clear sales count when component unmounts
      const sidebarCounts = JSON.parse(
        localStorage.getItem("sidebarCounts") || "{}"
      );
      delete sidebarCounts.sales;
      localStorage.setItem("sidebarCounts", JSON.stringify(sidebarCounts));
    };
  }, []);

  // In handleChangePage for pagination buttons, setCurrentPage and fetch data
  const handleChangePage = (newPage) => {
    setCurrentPage(newPage);
    // fetchFilteredData(filterValues); // Will be triggered by useEffect
  };

  // Add debounce search effect after searchInputValue definition
  useEffect(() => {
    if (!showTable) return;
    const handler = setTimeout(() => {
      fetchFilteredData(filterValues);
    }, 500); // 500ms debounce
    return () => clearTimeout(handler);
  }, [searchInputValue]);

  // Update getPaginationInfo to use totalElements
  const getPaginationInfo = () => {
    if (totalElements === 0) return { start: 0, end: 0, total: 0 };
    const start = (currentPage - 1) * rowsPerPage + 1;
    const end = Math.min(currentPage * rowsPerPage, totalElements);
    return { start, end, total: totalElements };
  };

  // Add a cleanup effect to clear the sidebar sales count on unmount
  useEffect(() => {
    return () => {
      updateTotalSales(0);
    };
  }, []);

  return (
    <>
      <Loader isLoading={isPageLoading || isLoadingEmployees || isSearching} />
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box className="flex flex-col bg-white  md:overflow-y-visible">
          <div className="p-2 sm:p-6">


            <Formik
              initialValues={filterValues}
              onSubmit={fetchFilteredData}
              enableReinitialize
            >
              {({ values, setFieldValue }) => (
                <Form className="mb-6">
                  <Box className="flex flex-col md:flex-row md:flex-nowrap items-center gap-3 md:gap-5">
                    <div className="lg:w-1/5 md:w-1/2 w-full">
                      <CustomDropdown
                        value={values.company}
                        name="company"
                        handleChange={async (val) => {
                          // First update the company
                          setFieldValue("company", val);
                          
                          // Clear current employee selection
                          setFieldValue("employee", "");
                          setEmployeeId(null);
                          
                          // Clear table data and update count
                          setShowTable(false);
                          setTotalCount(0);
                          updateTotalSales(0);
                          
                          // Then load new employees if company is selected
                          if (val?.value) {
                            const newEmployees = await loadEmployees(val.value);
                            if (newEmployees.length > 0) {
                              // Set the first employee as default
                              setFieldValue("employee", newEmployees[0]);
                              setEmployeeId(newEmployees[0].value);
                            }
                          }
                        }}
                        placeholder="Company"
                        options={companyOptions}
                        handleGetOptionValue={opt => opt.value}
                        handleGetOptionName={opt => opt.label}
                        customClasses="w-full"
                      />
                    </div>
                    <div className="lg:w-1/5 md:w-1/2 w-full">
                      <CustomDropdown
                        value={values.employee}
                        name="employee"
                        handleChange={val => {
                          setFieldValue("employee", val);
                          setEmployeeId(val?.value || null);
                          // Reset to page 1 when employee selection changes
                          setCurrentPage(1);
                          // Clear table data and update count
                          setShowTable(false);
                          setTotalCount(0);
                          updateTotalSales(0);
                        }}
                        placeholder="Consultants"
                        options={employeesOptions}
                        handleGetOptionValue={opt => opt.value}
                        handleGetOptionName={opt => opt.label}
                        customClasses="w-full"
                        isSearchable={true}
                      />
                    </div>
                    <div className="lg:w-1/5 md:w-1/2 w-full">
                      <CustomDropdown
                        value={values.submittedBy}
                        name="submittedBy"
                        handleChange={val => {
                          setFieldValue("submittedBy", val);
                          // Clear table data and update count
                          setShowTable(false);
                          setTotalCount(0);
                          updateTotalSales(0);
                        }}
                        placeholder="Submitted By"
                        options={submittedByOptions}
                        handleGetOptionValue={opt => opt.value}
                        handleGetOptionName={opt => opt.label}
                        customClasses="w-full"
                      />
                    </div>
                    <div className="lg:w-1/5 md:w-1/2 w-full">
                      <DatePicker
                        label="From Date"
                        value={values.fromDate}
                        onChange={val => {
                          setFieldValue("fromDate", val);
                          // Clear table data and update count
                          setShowTable(false);
                          setTotalCount(0);
                          updateTotalSales(0);
                        }}
                        slotProps={{ textField: { size: "small", fullWidth: true } }}
                      />
                    </div>
                    <div className="lg:w-1/5 md:w-1/2 w-full">
                      <DatePicker
                        label="To Date"
                        value={values.toDate}
                        onChange={val => {
                          setFieldValue("toDate", val);
                          // Clear table data and update count
                          setShowTable(false);
                          setTotalCount(0);
                          updateTotalSales(0);
                        }}
                        slotProps={{ textField: { size: "small", fullWidth: true } }}
                        minDate={values.fromDate}
                      />
                    </div>
                    <div className="w-full md:w-auto flex justify-end md:items-end md:self-end">
                      <button
                        type="submit"
                        className="h-10 w-full md:w-auto bg-[#201F1E] hover:bg-[#201F1E] text-white px-6 rounded-[4px] transition-all duration-300 flex items-center justify-center"
                      >
                        <FaSearch className="text-xl" />
                      </button>
                    </div>
                  </Box>
                </Form>
              )}
            </Formik>

            {showTable && (
              <div>
                
                <div className="overflow-hidden bg-white border border-gray-200 rounded-lg">
                  <div className="p-2 sm:p-4 border-b border-gray-200">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                      <div className="relative flex-1">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-search h-5 w-5 text-gray-400">
                            <circle cx="11" cy="11" r="8"></circle>
                            <path d="m21 21-4.3-4.3"></path>
                          </svg>
                        </div>
                        <input
                          type="text"
                          className="block w-full pl-10 pr-4 py-2 rounded-[4px] border border-[#D1D1D1] bg-white text-sm text-[#201F1E] placeholder-[#656464] focus:outline-none transition-colors duration-150"
                          placeholder="Search submissions by client name or location or vendor"
                          value={searchInputValue}
                          onChange={(e) => setSearchInputValue(e.target.value)}
                        />
                      </div>
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto min-w-0">
                        <CustomDropdown
                          value={statusOptions.find((option) => option.value === filterValues.status.value)}
                          name="status"
                          handleChange={(option) => handleStatusChange(option?.value || "all")}
                          placeholder="Status"
                          options={statusOptions}
                          handleGetOptionValue={(opt) => opt.value}
                          handleGetOptionName={(opt) => opt.label}
                          customClasses="w-full sm:w-40 min-w-0"
                        />
                        {filterValues.employee?.value !== "all" && filterValues.employee?.value && (
                          <button
                            onClick={() => setShowAddModal(true)}
                            className="h-[38px] px-4 bg-[#201F1E] hover:bg-[#201F1E] text-white text-sm font-medium rounded-[4px] transition-colors flex items-center justify-center whitespace-nowrap"
                          >
                            Add Submission
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: "58vh" }}>
                    <div className="hidden md:block overflow-x-auto">
                    <table className="min-w-[700px] divide-y divide-gray-200 table-fixed">
                      <thead className="bg-gray-50 sticky top-0 z-10">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-[#201F1E]   uppercase tracking-wider w-1/4">Client Name</th>
                          {filterValues.employee?.value === "all" && (
                            <th className="px-6 py-3 text-left text-xs font-medium text-[#201F1E] uppercase tracking-wider w-1/5">Consultant Name</th>
                          )}
                          <th className="px-6 py-3 text-left text-xs font-medium text-[#201F1E] uppercase tracking-wider w-1/6">Location</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-[#201F1E] uppercase tracking-wider w-1/6">Vendor</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-[#201F1E] uppercase tracking-wider w-1/6">Job Title</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-[#201F1E] uppercase tracking-wider w-1/8">Rate</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-[#201F1E] uppercase tracking-wider w-1/8">Notes</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-[#201F1E] uppercase tracking-wider w-1/8">Submitted By</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-[#201F1E] uppercase tracking-wider w-1/8">Update Date</th>
                          {filterValues.employee?.value !== "all" && (
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/8">Status</th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {(submissions || []).map((row) => {
                          const statusText = row.stsCd === "A" ? "Active" : "Deactive";
                          const statusStyles = row.stsCd === "A"
                            ? "bg-[rgba(52, 211, 153, 0.125)] text-[#34D399]"
                            : "bg-red-100 text-red-600";
                          const isDisabled = isMarketingExec && row.submittedBy !== currentUserFullName;
                          const isRowClickable = filterValues.employee?.value !== "all" && !isDisabled;
                          return (
                            <tr key={row.id}
                              onClick={isRowClickable ? () => handleView(row) : undefined}
                              className={isRowClickable ? 'cursor-pointer hover:bg-blue-50' : ''}
                            >
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="flex-shrink-0 h-10 w-10">
                                    <div className="h-10 w-10 rounded-full bg-gray-100 text-[#201F1E] flex items-center justify-center font-semibold">
                                      {getInitials(row.clientName)}
                                    </div>
                                  </div>
                                  <div className="ml-4">
                                    <div className="text-sm font-medium text-[#656464]">{row.clientName}</div>
                                  </div>
                                </div>
                              </td>
                              {filterValues.employee?.value === "all" && (
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-[#656464]" style={{ minWidth: 130, whiteSpace: 'nowrap' }}>{row.consultantName || "N/A"}</div>
                                </td>
                              )}
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-[#656464]">
                                  <div className="truncate max-w-[100px]" data-tip data-for={`locationTip-${row.id}`} title={row.clientLocation}>{row.clientLocation}</div>
                                  <CustomToolTip id={`locationTip-${row.id}`} title={row.clientLocation || "N/A"} />
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-[#656464]">{row.vendor || "N/A"}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-[#656464] max-w-[200px] truncate">
                                  <div className="truncate max-w-[100px]" data-tip data-for={`jobTitleTip-${row.id}`}>{row.jobTitle}</div>
                                  <CustomToolTip id={`jobTitleTip-${row.id}`} title={row.jobTitle || "N/A"} />
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-[#656464]">{row.rate}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-[#656464]">
                                  <div className="truncate max-w-[100px]" data-tip data-for={`notesTip-${row.id}`}>{row.notes || "N/A"}</div>
                                  <CustomToolTip id={`notesTip-${row.id}`} title={row.notes || "N/A"} />
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-[#656464]">{row.submittedBy}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-[#656464]" style={{ minWidth: 90, whiteSpace: 'nowrap' }}>{row.updatedDate ? format(new Date(row.updatedDate), "MM-dd-yyyy") : ""}</div>
                              </td>
                              {filterValues.employee?.value !== "all" && (
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${row.stsCd === 'A' ? 'bg-[#34D39920] text-[#34D399]' : 'bg-red-50 text-red-400'}`}>{row.stsCd === 'A' ? 'Active' : 'Closed'}</span>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    </div>

                    <div className="md:hidden px-2 pb-2 space-y-4">
                      {(submissions || []).map((row) => {
                        const isDisabled = isMarketingExec && row.submittedBy !== currentUserFullName;
                        const isRowClickable = filterValues.employee?.value !== "all" && !isDisabled;
                        return (
                          <article
                            key={row.id}
                            className={`${MOBILE_LV_CARD_CLASS}${isRowClickable ? " cursor-pointer active:opacity-90" : ""}`}
                            onClick={isRowClickable ? () => handleView(row) : undefined}
                            role={isRowClickable ? "button" : undefined}
                          >
                            <div className={MOBILE_LV_GRID_CLASS}>
                              <div className={MOBILE_LV_LABEL_CLASS}>Client Name</div>
                              <div className={MOBILE_LV_VALUE_CLASS}>
                                <div className="flex items-center gap-2">
                                  {/* <div className="h-9 w-9 shrink-0 rounded-full bg-gray-100 text-[#201F1E] flex items-center justify-center text-xs font-semibold">
                                    {getInitials(row.clientName)}
                                  </div> */}
                                  <span className="text-[#201F1E]">{row.clientName}</span>
                                </div>
                              </div>
                              {filterValues.employee?.value === "all" && (
                                <>
                                  <div className={MOBILE_LV_LABEL_CLASS}>Consultant Name</div>
                                  <div className={MOBILE_LV_VALUE_CLASS}>{row.consultantName || "N/A"}</div>
                                </>
                              )}
                              <div className={MOBILE_LV_LABEL_CLASS}>Location</div>
                              <div className={MOBILE_LV_VALUE_CLASS}>{row.clientLocation}</div>
                              <div className={MOBILE_LV_LABEL_CLASS}>Vendor</div>
                              <div className={MOBILE_LV_VALUE_CLASS}>{row.vendor || "N/A"}</div>
                              <div className={MOBILE_LV_LABEL_CLASS}>Job Title</div>
                              <div className={MOBILE_LV_VALUE_CLASS}>{row.jobTitle}</div>
                              <div className={MOBILE_LV_LABEL_CLASS}>Rate</div>
                              <div className={MOBILE_LV_VALUE_CLASS}>{row.rate}</div>
                              <div className={MOBILE_LV_LABEL_CLASS}>Notes</div>
                              <div className={MOBILE_LV_VALUE_CLASS} title={row.notes}>{row.notes || "N/A"}</div>
                              <div className={MOBILE_LV_LABEL_CLASS}>Submitted By</div>
                              <div className={MOBILE_LV_VALUE_CLASS}>{row.submittedBy}</div>
                              {filterValues.employee?.value !== "all" ? (
                                <>
                                  <div className={MOBILE_LV_LABEL_CLASS}>Update Date</div>
                                  <div className={MOBILE_LV_VALUE_CLASS}>
                                    {row.updatedDate ? format(new Date(row.updatedDate), "MM-dd-yyyy") : ""}
                                  </div>
                                  <div className={`${MOBILE_LV_LABEL_CLASS}${MOBILE_LV_LABEL_LAST}`}>Status</div>
                                  <div className={`${MOBILE_LV_VALUE_CLASS}${MOBILE_LV_VALUE_LAST}`}>
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${row.stsCd === 'A' ? 'bg-[#34D39920] text-[#34D399]' : 'bg-red-50 text-red-400'}`}>
                                      {row.stsCd === 'A' ? 'Active' : 'Closed'}
                                    </span>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div className={`${MOBILE_LV_LABEL_CLASS}${MOBILE_LV_LABEL_LAST}`}>Update Date</div>
                                  <div className={`${MOBILE_LV_VALUE_CLASS}${MOBILE_LV_VALUE_LAST}`}>
                                    {row.updatedDate ? format(new Date(row.updatedDate), "MM-dd-yyyy") : ""}
                                  </div>
                                </>
                              )}
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  </div>
                  <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                    <div className="flex-1 flex justify-between sm:hidden">
                      <button
                        onClick={() => currentPage > 1 && handleChangePage(1)}
                        disabled={currentPage === 1 || totalCount === 0}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                      >
                        First
                      </button>
                      <button
                        onClick={() => currentPage > 1 && handleChangePage(currentPage - 1)}
                        disabled={currentPage === 1 || totalCount === 0}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => currentPage < totalPages && handleChangePage(currentPage + 1)}
                        disabled={currentPage === totalPages || totalCount === 0}
                        className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                      <button
                        onClick={() => currentPage < totalPages && handleChangePage(totalPages)}
                        disabled={currentPage === totalPages || totalCount === 0}
                        className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                      >
                        Last
                      </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-gray-700">
                          Showing <span className="font-medium">{getPaginationInfo().start}</span> to <span className="font-medium">{getPaginationInfo().end}</span> of <span className="font-medium">{getPaginationInfo().total}</span> submissions
                        </p>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                          <label htmlFor="rows-per-page" className="text-sm text-gray-700">
                            Rows per page:
                          </label>
                          <select
                            id="rows-per-page"
                            value={rowsPerPage}
                            onChange={e => handleRowsPerPageChange(Number(e.target.value))}
                            className="px-2 py-1 text-sm border border-gray-300 rounded-md bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                          </select>
                        </div>
                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                          <button
                            onClick={() => currentPage > 1 && handleChangePage(1)}
                            disabled={currentPage === 1 || totalCount === 0}
                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                          >
                            First
                          </button>
                          <button
                            onClick={() => currentPage > 1 && handleChangePage(currentPage - 1)}
                            disabled={currentPage === 1 || totalCount === 0}
                            className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                          >
                            Previous
                          </button>
                          {totalCount > 0 && Array.from({ length: totalPages }, (_, i) => i + 1).slice(Math.max(0, currentPage - 3), currentPage + 2).map(pageNum => (
                            <button
                              key={pageNum}
                              onClick={() => handleChangePage(pageNum)}
                              disabled={totalCount === 0}
                              className={`relative inline-flex items-center px-4 py-2 border border-gray-300 ${
                                pageNum === currentPage
                                  ? 'bg-blue-50 text-blue-600'
                                  : 'bg-white text-gray-700'
                              } text-sm font-medium hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed`}
                            >
                              {pageNum}
                            </button>
                          ))}
                          <button
                            onClick={() => currentPage < totalPages && handleChangePage(currentPage + 1)}
                            disabled={currentPage === totalPages || totalCount === 0}
                            className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                          >
                            Next
                          </button>
                          <button
                            onClick={() => currentPage < totalPages && handleChangePage(totalPages)}
                            disabled={currentPage === totalPages || totalCount === 0}
                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                          >
                            Last
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <AddClientModal
              show={showAddModal}
              onClose={() => setShowAddModal(false)}
              title="Add Submission"
              initialValues={clientInitialValues}
              onAddClient={handleAddClient}
            />

            <EditClientModal
              show={showEditModal}
              onClose={() => setShowEditModal(false)}
              title={isEditMode ? "Edit Submission" : "View Submission"}
              initialValues={{
                id: editClientData?.id || "",
                clientName: editClientData?.clientName || "",
                clientLocation: editClientData?.clientLocation || "",
                jobTitle: editClientData?.jobTitle || "",
                rate: editClientData?.rate || "",
                technology: editClientData?.technology || "",
                status: editClientData?.stsCd || "A",
                notes: editClientData?.notes || "",
                tempEmpId: editClientData?.tempEmpId || employeeId || "",
                vendor: editClientData?.vendor || ""
              }}
              onEditClient={handleEditClient}
              isEditMode={isEditMode}
              onEditClick={() => setIsEditMode(true)}
            />
          </div>
         
        </Box>
      </LocalizationProvider>
    </>
  );
};

export default Hoc(Sales);